import { type UIMessage } from '@ai-sdk/react'
import { motion } from 'framer-motion'
import { ArrowUp, ChevronDown, ImagePlus } from 'lucide-react'
import { Heading, Input } from 'components'
import { ChatDropOverlay } from 'modules/creative/components/ChatDropOverlay'
import { ChatPendingThumbnails } from 'modules/creative/components/ChatPendingThumbnails'
import { MarkdownContent } from 'modules/creative/components/MarkdownContent'
import { useChatFileDrop } from 'modules/creative/hooks/useChatFileDrop'
import { useCreativeChat } from 'modules/creative/hooks/useCreativeChat'
import { MAX_CHAT_ATTACHMENTS } from 'modules/creative/hooks/usePendingAttachments'
import {
  OPTIONS_RE,
  type Part,
  SUBJECT_RE,
  parseOptions,
  partsText,
} from 'modules/creative/utils/chat-markers'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  getProgressVisualProps,
  buildInterviewChrome,
  SKIP_LABEL,
  type InterviewChromeState,
} from './conversation-chrome'
import { LiquidProgressBar } from './LiquidProgressBar'
import { FILL_STEPS, TOTAL_STEPS } from './LogoStepProgress'

type Msg = { id: string; role: string; parts?: Part[] }

/** Brand edits during onboarding that should refresh the hub list / brand kit. */
const REFRESH_TOOLS = new Set([
  'captureBrandMemory',
  'patchBrandCard',
  'setBrandTone',
  'setBrandColors',
  'setBrandLogo',
])

/** Strip the subject + options markers from a text. */
function cleanText(text: string): string {
  return (
    text
      .replace(SUBJECT_RE, '')
      .replace(OPTIONS_RE, '')
      // Drop a trailing half-streamed marker (e.g. "[[subj", "[[options: a |") so
      // brackets never flicker in raw while the turn is still typing out.
      .replace(/\[\[[^\]]*$/, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  )
}

/** Blank-line-separated blocks → one chat bubble each. */
function toBlocks(text: string): string[] {
  return text
    ? text
        .split(/\n{2,}/)
        .map(b => b.trim())
        .filter(Boolean)
    : []
}

/** Normalized tool parts on a message — handles both `tool-<name>` types and the
 * legacy `toolName` field, and whether the result has landed. One place so the
 * memory card, the tool-activity ping, and completion detection share it. */
function toolResults(
  parts?: Part[]
): { name: string; done: boolean; input?: Record<string, unknown> }[] {
  if (!Array.isArray(parts)) return []
  const out: { name: string; done: boolean; input?: Record<string, unknown> }[] = []
  for (const p of parts) {
    const type = typeof p.type === 'string' ? p.type : ''
    const name =
      typeof p.toolName === 'string' ? p.toolName : type.startsWith('tool-') ? type.slice(5) : ''
    if (!name) continue
    const state = typeof p.state === 'string' ? p.state : ''
    out.push({
      name,
      done: !state || state === 'output-available',
      input: (p.input ?? p.args) as Record<string, unknown> | undefined,
    })
  }
  return out
}

/** Pulls the captured memory bullets from an assistant message's tool parts. */
function extractMemories(parts?: Part[]): string[] {
  const out: string[] = []
  for (const t of toolResults(parts)) {
    if (t.name !== 'captureBrandMemory' || !t.done) continue
    const mems = (t.input?.memories as Array<{ text?: string }> | undefined) ?? []
    for (const mem of mems)
      if (typeof mem?.text === 'string' && mem.text.trim()) out.push(mem.text.trim())
    if (typeof t.input?.text === 'string' && t.input.text.trim()) out.push(t.input.text.trim())
  }
  return out
}

export interface ConversationInterviewProps {
  /** Agent variant — the mobile, no-confirm guided onboarding interview. */
  intent: 'onboarding_conversation'
  /** useChat id, distinct per surface so threads don't collide. */
  chatId: string
  /** Send the hidden seed once the business is ready so the agent opens. */
  seedOnMount: boolean
  /** Brand name, used to warm up the first-load state before Taka's first turn. */
  brandName?: string
  /** Hidden seed steering the agent (skip re-confirm, emit [[subject:]] etc.). */
  seedMessage: string
  /** Any tool result may have captured a memory / patched the brand — refresh. */
  onToolActivity?: () => void
  /** Surface an unrecoverable agent error so the parent can fall back. */
  onAgentError?: () => void
  /** Tapping the "Skip" quick-reply leaves the interview (advance onboarding). */
  onSkip?: () => void
  /** Lifted chrome for the desktop progress hub (saved count, phases). */
  onInterviewStateChange?: (state: InterviewChromeState) => void
  /** Persist the interview server-side (non-ephemeral) so it can be resumed. */
  persist?: boolean
  /** Hydrated history from GET /onboarding-chat; suppresses the seed when present. */
  initialMessages?: UIMessage[]
}

const EASE = [0.2, 0.8, 0.2, 1] as const

// Two distinct chat bubbles so the sides read clearly. Taka (incoming, left): a
// soft borderless surface with a top-left tail. You (outgoing, right): a brighter
// edged surface with a top-right tail. Shared base keeps them in lockstep.
const BUBBLE_BASE = 't-body w-fit max-w-[85%] whitespace-pre-wrap rounded-[16px] px-3.5 py-2.5'
// Taka (incoming): neutral dark bubble. You (outgoing): soft acid-tinted bubble
// so your answers carry the brand's accent (the only T3 accent).
const ASSISTANT_BUBBLE = `${BUBBLE_BASE} bg-bg-3 border-line rounded-tl-[5px] border`
const USER_BUBBLE = `${BUBBLE_BASE} bg-acid-soft border-acid/30 rounded-tr-[5px] border`

// Classic three-dot "typing" indicator — a far warmer first-load signal than a
// bare "Thinking" label. Staggered bounce reads instantly as "someone's typing".
function TypingDots() {
  return (
    <span className="flex items-center gap-1 py-0.5">
      <span className="size-1.5 animate-bounce rounded-full bg-paper-dim [animation-delay:-300ms]" />
      <span className="size-1.5 animate-bounce rounded-full bg-paper-dim [animation-delay:-150ms]" />
      <span className="size-1.5 animate-bounce rounded-full bg-paper-dim" />
    </span>
  )
}

// A short curated "reasoning" beat — a line of what Taka's working through plus a
// typing pulse — shown right before a question lands. Cycles its lines and holds
// on the last. Curated on purpose (not raw model thoughts), so it never leaks the
// interview's internal markers or tool mechanics.
function ProgressBeat({ lines }: { lines: string[] }) {
  const [i, setI] = useState(0)
  useEffect(() => {
    if (i >= lines.length - 1) return
    const t = setTimeout(() => setI(n => n + 1), 1100)
    return () => clearTimeout(t)
  }, [i, lines.length])
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span className="shimmer-text text-[13px] leading-snug">{lines[i]}</span>
      <TypingDots />
    </div>
  )
}

// The chat's opening greeting — a warm, instant client-rendered hello anchored at
// the top of the thread so the conversation never opens cold (and never waits on
// the model to say hi). The agent's own first message is the question; this is
// just Taka introducing itself.
function OpeningGreeting({ brandName }: { brandName?: string }) {
  const brand = brandName?.trim() || 'your brand'
  const bubbles = [`Let me get to know ${brand}, so every post I make actually sounds like you.`]
  return (
    <div className="flex flex-col items-start gap-2">
      {bubbles.map((b, i) => (
        // Staggered entrance so the opener lands like real chat bubbles, one
        // after the other, rather than appearing all at once.
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: EASE, delay: 0.15 + i * 0.5 }}
          className={ASSISTANT_BUBBLE}
        >
          {b}
        </motion.div>
      ))}
    </div>
  )
}

// The one acid CTA recipe, used by the "Continue" button when the interview ends.
const PRIMARY_CTA =
  'bg-acid text-ink-on-acid hover:bg-acid/90 w-full rounded-[12px] px-4 py-3.5 text-[15px] font-semibold transition-colors'

/**
 * Agent-driven onboarding interview over the shared `/v1/creative/chat` agent,
 * rendered as a real chat. Taka asks one grounded question at a time (with a
 * `[[subject:]]` label + `[[options:]]` picks); the user answers by tapping an
 * option or typing, OR asks back — Taka replies naturally either way. When the
 * agent captures something, a "saved to memory" card shows under that turn. The
 * current question is shown prominently at the bottom (newest, by the input);
 * older turns scroll above. The agent moving to the next topic IS the advance —
 * there is no manual Continue button (which would break clarifications).
 */
export function ConversationInterview({
  intent,
  chatId,
  seedOnMount,
  brandName,
  seedMessage,
  onToolActivity,
  onAgentError,
  onSkip,
  onInterviewStateChange,
  persist,
  initialMessages,
}: ConversationInterviewProps) {
  // Photo upload — useChatFileDrop owns local preview + per-item upload; the
  // attachmentIds for the NEXT message ride in a ref the (per-request) transport
  // body reads. The server auto-saves them to the brand kit for this intent
  // (intentPersistsBrandImages).
  const {
    pending,
    removeAt,
    clear,
    hasUploading,
    ready,
    fileInputRef,
    attachmentIdsRef,
    isDragging,
    dragHandlers,
    handleFiles,
  } = useChatFileDrop()

  // Shared chat engine: transport + useChat + the hidden-seed opener + error
  // surfacing. Rendering and send semantics stay local to this component.
  const { messages, sendMessage, isLoading, seedMessageId } = useCreativeChat({
    intent,
    chatId,
    seedOnMount,
    seedMessage,
    onAgentError,
    attachmentIdsRef,
    persist,
    initialMessages,
  })

  // Which "Saved" notes are expanded into the full running list of captures.
  const [openSaves, setOpenSaves] = useState<Set<string>>(() => new Set())
  const toggleSave = useCallback((id: string) => {
    setOpenSaves(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])
  // Staged opening: status ("what we're doing") → greeting + a typing beat →
  // the first question drops in. `firstRevealed` flips after the greeting lands.
  const [firstRevealed, setFirstRevealed] = useState(false)

  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  // Completed brand-edit tools (not completeInterview) — refresh hub list + kit.
  const sawToolRef = useRef(0)
  useEffect(() => {
    const toolCount = (messages as Msg[])
      .filter(m => m.role === 'assistant')
      .reduce(
        (n, m) => n + toolResults(m.parts).filter(t => REFRESH_TOOLS.has(t.name) && t.done).length,
        0
      )
    if (toolCount > sawToolRef.current) {
      sawToolRef.current = toolCount
      onToolActivity?.()
    }
  }, [messages, onToolActivity])

  // Everything except the hidden seed. We KEEP the in-flight assistant message so
  // it streams in live (types out) rather than popping in all at once — cleanText
  // drops any half-typed trailing marker so [[… never flickers raw.
  const transcript = useMemo(
    () => (messages as Msg[]).filter(m => m.id !== seedMessageId),
    [messages, seedMessageId]
  )

  // The latest assistant turn owns the live quick-reply chips (older turns are
  // already answered, so their options don't reappear).
  const lastAssistantId = useMemo(() => {
    for (let i = transcript.length - 1; i >= 0; i--) {
      if (transcript[i].role === 'assistant') return transcript[i].id
    }
    return null
  }, [transcript])

  // The opening turn (the agent's greeting + first question) and whether we're
  // still in it — used to stage the open with a status → greeting → question beat.
  const firstAssistantId = useMemo(
    () => transcript.find(m => m.role === 'assistant')?.id ?? null,
    [transcript]
  )
  const inOpening = useMemo(() => !transcript.some(m => m.role === 'user'), [transcript])

  const lastMsg = transcript[transcript.length - 1]
  const waitingForResponse =
    isLoading && (!lastMsg || lastMsg.role === 'user' || partsText(lastMsg.parts).trim() === '')
  // We're still warming up the opening question: the greeting is on screen and we
  // show a reasoning beat beneath it until the agent's first question has landed
  // AND its hold has elapsed (firstRevealed). Covers the background business
  // create, the streaming wait, and the deliberate beat before the reveal.
  const openingPending = inOpening && !firstRevealed

  // Scroll only the inner chat area (not the page) to the newest content.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [transcript, isLoading])

  // Once the opening turn has fully landed, hold the greeting on screen with a
  // typing beat, then drop in the first question. Opening turn only.
  useEffect(() => {
    if (firstRevealed || !firstAssistantId || isLoading || !inOpening) return
    const t = setTimeout(() => setFirstRevealed(true), 2400)
    return () => clearTimeout(t)
  }, [firstRevealed, firstAssistantId, isLoading, inOpening])

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if ((!trimmed && ready.length === 0) || isLoading || hasUploading) return
      setInput('')
      // Attach this turn's uploaded photo ids for the server (read by the
      // transport body), then clear them once the request is sent.
      attachmentIdsRef.current = ready.map(p => p.attachmentId).filter((id): id is string => !!id)
      const count = attachmentIdsRef.current.length
      const body = trimmed || `I added ${count} photo${count > 1 ? 's' : ''} for my brand.`
      clear()
      void sendMessage({ text: body }).finally(() => {
        attachmentIdsRef.current = []
      })
    },
    [isLoading, hasUploading, ready, clear, sendMessage, attachmentIdsRef]
  )

  // Completion = the agent called the `completeInterview` tool (more reliable than
  // a text marker). Derived from the transcript: the tool part persists in the
  // message history, so this stays true once seen — no separate latch needed. It
  // fires when the agent ASKS the final question (which may get no reply), so
  // Continue shows regardless of whether the user answers.
  const doneActive = useMemo(
    () =>
      transcript.some(
        m =>
          m.role === 'assistant' &&
          toolResults(m.parts).some(t => t.name === 'completeInterview' && t.done)
      ),
    [transcript]
  )

  // Liquid-fill progress (0–6) for the onboarding logo. One tier per TOPIC whose
  // answer the agent actually CAPTURED (captureBrandMemory). Capture = a real
  // answer: a clarification / question-back ("I don't understand") gets a plain
  // reply with no capture, so it never fills; a dig follow-up on the same topic
  // doesn't double-count (the topic is credited once). The agent captures BEFORE
  // it writes the next [[subject:]], so the fill still rises a beat ahead of the
  // next question. Logo fills to 100% at FILL_STEPS; bar uses TOTAL_STEPS tiers;
  // completeInterview snaps both to TOTAL_STEPS (full).
  const answeredSubjects = useMemo(() => {
    let count = 0
    let pending = false // a subject is open, its answer not yet captured
    for (const m of transcript) {
      if (m.role !== 'assistant') continue
      const captured = toolResults(m.parts).some(t => t.name === 'captureBrandMemory' && t.done)
      if (pending && captured) {
        count++
        pending = false
      }
      // A new subject opens the next pending topic (after crediting any capture
      // on this same advance turn).
      if (SUBJECT_RE.test(partsText(m.parts))) pending = true
    }
    return count
  }, [transcript])
  // Monotonic: the liquid only ever RISES within an interview — it never drains.
  // Fill rises when captureBrandMemory lands (not optimistically on tap).
  // doneActive snaps to 6 (full + calm).
  const target = doneActive ? TOTAL_STEPS : Math.min(FILL_STEPS, answeredSubjects)
  const maxStepRef = useRef(0)
  if (target > maxStepRef.current) maxStepRef.current = target
  const progressStep = maxStepRef.current

  const handleOptionTap = useCallback(
    (opt: string) => {
      if (opt.trim().toLowerCase() === 'skip' && onSkip) {
        onSkip()
        return
      }
      send(opt)
    },
    [onSkip, send]
  )

  const savedCount = useMemo(
    () =>
      transcript.reduce(
        (n, m) => (m.role === 'assistant' ? n + extractMemories(m.parts).length : n),
        0
      ),
    [transcript]
  )

  const hasUserReply = useMemo(() => transcript.some(m => m.role === 'user'), [transcript])
  const warmup = !hasUserReply && (openingPending || !firstAssistantId)
  const frozen = !hasUserReply && !warmup && firstRevealed
  const engaged = hasUserReply

  const interviewChrome = useMemo(
    () =>
      buildInterviewChrome({
        progressStep,
        savedCount,
        doneActive,
        warmup,
        frozen,
        engaged,
      }),
    [progressStep, savedCount, doneActive, warmup, frozen, engaged]
  )

  const progressVisual = useMemo(() => getProgressVisualProps(interviewChrome), [interviewChrome])

  useEffect(() => {
    onInterviewStateChange?.(interviewChrome)
  }, [interviewChrome, onInterviewStateChange])

  // The latest assistant step has no quick-reply chips, so it wants free input.
  const lastAssistantText = lastMsg?.role === 'assistant' ? partsText(lastMsg.parts) : ''
  const awaitingFreeInput =
    !isLoading && lastMsg?.role === 'assistant' && parseOptions(lastAssistantText).length === 0
  // The photos step asks the user to ATTACH images — point them at the attach
  // button (acid outline) instead of the text input.
  const awaitingPhotos =
    awaitingFreeInput && /\b(photo|photos|image|images|attach|drag)\b/i.test(lastAssistantText)
  // A plain open question wants a TYPED answer — outline the input instead.
  const awaitingTyped = awaitingFreeInput && !awaitingPhotos

  // The send button is live (and acid) only when there's actually something to
  // send — text typed or a photo ready — and we're not mid-request/upload.
  const canSend = !isLoading && !hasUploading && (input.trim().length > 0 || ready.length > 0)

  // A quiet "Saved" link tacked onto the END of a turn that captured something —
  // no captured text shown inline. Tap it to expand the full running list of
  // everything saved so far (up to and including this turn). No acid (stays scarce).
  const renderSaved = (msg: Msg) => {
    const memories = extractMemories(msg.parts)
    if (memories.length === 0) return null
    const open = openSaves.has(msg.id)
    // Everything captured from the first turn through this one (inclusive).
    const all: string[] = []
    for (const m of transcript) {
      if (m.role === 'assistant') all.push(...extractMemories(m.parts))
      if (m.id === msg.id) break
    }
    return (
      <div className="w-full lg:hidden">
        <button
          type="button"
          onClick={() => toggleSave(msg.id)}
          className="group t-body-sm inline-flex items-center gap-1.5 pt-0.5 font-medium transition-colors hover:text-paper"
        >
          Added to your brand kit
          <ChevronDown
            size={14}
            className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {open && (
          <div className="mt-1.5 flex flex-col gap-1.5 rounded-[10px] border border-line bg-bg-2 p-2.5">
            <span className="t-body-sm font-medium">In your brand kit · {all.length}</span>
            {all.map((m, i) => (
              <div key={i} className="t-body-sm flex gap-1.5">
                <span className="text-paper-faint">•</span>
                <span>{m}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // One assistant turn. Reads as chat: each blank-line block is its own bubble.
  // A turn that opens a NEW subject gets a small topic divider above its question
  // (the only "title" — everything else stays conversational). The newest turn
  // carries the quick-reply chips for its options.
  const renderAssistant = (msg: Msg) => {
    const typed = msg as { id: string; role: string; parts: Part[] }
    const text = partsText(typed.parts)
    const subjMatch = text.match(SUBJECT_RE)
    const subject = subjMatch?.[1]?.trim() ?? null
    const idx = subjMatch?.index ?? -1
    // Anything before the [[subject:]] marker is conversational lead-in (the
    // opener's greeting + aim); the question is the subject block itself.
    const preamble = idx > 0 ? text.slice(0, idx).trim() : ''
    const question = cleanText(idx >= 0 ? text.slice(idx) : text)
    const isLast = typed.id === lastAssistantId
    // Chips only once the turn has finished streaming — never mid-type. The opener
    // greeting's options render as CTA buttons in the bar instead, so skip them here.
    const options = isLast && !isLoading ? parseOptions(text) : []
    // "Saved" position depends on whether this turn opened a subject — which flips
    // null→set mid-stream. Only render it once the turn is settled so it appears
    // once in its final spot instead of jumping bottom↔after-reaction while typing.
    const settled = !(isLast && isLoading)

    return (
      <motion.div
        key={typed.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: EASE }}
        className="flex flex-col items-start gap-2"
      >
        {toBlocks(preamble).map((b, i) => (
          <div key={`p-${i}`} className={ASSISTANT_BUBBLE}>
            <MarkdownContent>{b}</MarkdownContent>
          </div>
        ))}

        {/* "Saved" sits right after the REACTION. On an advance turn (reaction +
            new subject) that's here — after the preamble, before the new question,
            so it never drifts below the next question's options. */}
        {settled && subject && renderSaved(typed)}

        {/* A new subject is the step's QUESTION rendered as a style-guide heading
            (T3 t-h2) — the title for the topic. Follow-ups/reactions stay as
            bubbles, so a heading clearly signals "new question". */}
        {subject && (
          <Heading level="h2" as="h2" className="px-0.5 pt-2 pb-0.5">
            {subject}
          </Heading>
        )}

        {toBlocks(question).map((b, i) => (
          <div key={`q-${i}`} className={ASSISTANT_BUBBLE}>
            <MarkdownContent>{b}</MarkdownContent>
          </div>
        ))}

        {options.length > 0 && (
          // All options grouped under ONE outline, separated by hairlines; rows
          // highlight on hover (overflow-hidden clips the hover to the rounding).
          <div className="mt-1 w-full divide-y divide-line overflow-hidden rounded-[12px] border border-line bg-bg-2">
            {options.map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => handleOptionTap(opt)}
                className="group flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-bg-3"
              >
                {/* Checkbox, idle "waiting for click" — acid outline, soft acid fill on hover. */}
                <span className="group-hover:bg-acid-soft size-[18px] shrink-0 rounded-[4px] border border-acid transition-colors" />
                <span className="t-body flex-1">{opt}</span>
              </button>
            ))}
            {/* The picks are just shortcuts — a closing hairline row, inside the same
                outline, makes it explicit that a typed answer is always welcome (the
                input below isn't obviously "instead of these"). Not a button. */}
            <span className="t-body-sm block px-3 py-3 text-paper-dim">
              Or just tell me in your own words below.
            </span>
          </div>
        )}

        {/* On a dig turn (reaction + follow-up, no new subject) the reaction IS the
            whole turn, so "Saved" belongs here at the end. */}
        {settled && !subject && renderSaved(typed)}
      </motion.div>
    )
  }

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden" {...dragHandlers}>
      {isDragging && <ChatDropOverlay pendingCount={pending.length} />}

      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-3 py-3"
      >
        {/* One continuous chat, top-aligned: the conversation starts at the top
            and grows downward (auto-scrolls to the newest as it fills). */}
        <div className="flex flex-col gap-4">
          {/* Instant greeting anchors the top of the chat. */}
          <OpeningGreeting brandName={brandName} />

          {openingPending ? (
            // Reasoning beat under the greeting — fades in AFTER the greeting
            // bubbles land, then holds until the first question is revealed.
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 1 }}
            >
              <ProgressBeat
                lines={[
                  `Getting to know ${brandName?.trim() || 'your brand'}`,
                  'Thinking about what makes you distinct',
                  'Lining up your first question',
                ]}
              />
            </motion.div>
          ) : (
            <>
              {transcript.map(msg => {
                if (msg.role === 'assistant') return renderAssistant(msg)
                const text = partsText(msg.parts).trim()
                if (!text) return null
                return (
                  <div key={(msg as Msg).id} className="flex justify-end">
                    <div className={USER_BUBBLE}>
                      <MarkdownContent>{text}</MarkdownContent>
                    </div>
                  </div>
                )
              })}

              {waitingForResponse && (
                <ProgressBeat lines={['Thinking about that', 'Lining up the next question']} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Pinned bottom bar: attach + reply, Continue when the interview is done.
          A Skip is always available to leave the interview early. */}
      <div className="shrink-0 border-t border-line px-3 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        {/* Pending photo thumbnails — local preview + per-item upload status. */}
        <ChatPendingThumbnails pending={pending} onRemove={removeAt} />

        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Add photo"
            onClick={() => fileInputRef.current?.click()}
            disabled={pending.length >= MAX_CHAT_ATTACHMENTS}
            className={`flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-[10px] border bg-bg-3 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              awaitingPhotos
                ? 'border-primary! text-acid'
                : 'border-line-bright text-paper-dim hover:text-paper'
            }`}
          >
            <ImagePlus size={18} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
          {/* Style-guide dark Input (1px line at rest, 1px acid on focus). When the
              step needs a typed answer, force the acid border to point the user here. */}
          <Input
            variant="dark"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                send(input)
              }
            }}
            placeholder="Type a reply…"
            className={awaitingTyped ? 'border-primary!' : undefined}
          />
          <button
            type="button"
            aria-label="Send"
            onClick={() => send(input)}
            disabled={!canSend}
            className={
              canSend
                ? 'text-ink-on-acid flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-[10px] bg-acid transition-colors hover:bg-acid/90'
                : 'flex h-[48px] w-[48px] shrink-0 cursor-not-allowed items-center justify-center rounded-[10px] border border-line-bright bg-bg-3 text-paper-faint transition-colors'
            }
          >
            <ArrowUp size={18} strokeWidth={2.4} />
          </button>
        </div>

        {/* Done: full-width Continue. Before that: mobile liquid bar + Skip (desktop hub owns Skip). */}
        {doneActive ? (
          <button type="button" onClick={() => onSkip?.()} className={`${PRIMARY_CTA} mt-2.5`}>
            Continue
          </button>
        ) : (
          onSkip && (
            <div className="mt-3 flex items-center gap-3 lg:hidden">
              <LiquidProgressBar
                step={progressVisual.step}
                animate={progressVisual.animate}
                className="min-w-0 flex-1"
              />
              <button
                type="button"
                onClick={() => onSkip()}
                className="link-muted t-body-sm shrink-0 whitespace-nowrap"
              >
                {SKIP_LABEL}
              </button>
            </div>
          )
        )}
      </div>
    </div>
  )
}
